import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Truck, Zap, Clock, MapPin, Check, Sparkles, ArrowRight } from "lucide-react";

const GenfindLandingPage = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  const goToBooking = () => {
    navigate("/book?source=genfind&promo=GENFIND5");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1128] via-[#0D1635] to-[#0A1128] text-white" data-testid="genfind-landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-20 pb-24">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-red-500 rounded-full filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 max-w-6xl">
          {/* Co-branded header */}
          <div className="flex items-center justify-center gap-6 mb-10" data-testid="cobrand-header">
            <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
              <span className="font-display font-bold text-2xl text-white">Genfind</span>
              <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">.eu</span>
            </div>
            <div className="text-3xl text-white/40 font-thin">×</div>
            <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
              <span className="font-display font-bold text-2xl text-white">EMA</span>
              <span className="text-red-400 text-xs font-bold uppercase tracking-widest">Kurer</span>
            </div>
          </div>

          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-6 bg-emerald-500/20 text-emerald-300 border-emerald-400/50 px-4 py-2 text-sm uppercase tracking-wider" data-testid="genfind-badge">
              <Sparkles className="w-4 h-4 mr-2 inline" />
              Eksklusivt for Genfind-kunder
            </Badge>

            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-tight mb-6" data-testid="genfind-hero-title">
              Send det <span className="text-emerald-400">grønt</span>.<br/>
              <span className="text-red-400">5% rabat</span> til dig.
            </h1>

            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
              Som Genfind-kunde får du <strong className="text-white">automatisk 5% rabat</strong> på din første samme-dag levering med EMA Kurer — vi henter din pakke direkte og kører den uden mellemstationer.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={goToBooking}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-7 rounded-2xl text-lg font-bold shadow-xl shadow-emerald-500/30 transition-all hover:scale-105 hover:shadow-emerald-500/50"
                data-testid="genfind-cta-book"
              >
                Book med 5% rabat
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
              <div className="text-sm text-white/50">
                Rabatkode: <code className="px-2 py-1 bg-white/10 rounded text-emerald-300 font-mono">GENFIND5</code> · 1 brug pr. kunde
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing zones */}
      <section className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="font-display font-bold text-4xl sm:text-5xl mb-4" data-testid="zones-title">
            <span className="text-emerald-400">Faste priser</span> i hele Sjælland
          </h2>
          <p className="text-white/60 text-lg">
            Klare zone-priser. Ingen skjulte gebyrer. Altid samme dag.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-testid="zones-grid">
          {/* Zone 1 */}
          <Card
            className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 backdrop-blur-sm border-emerald-400/30 p-8 rounded-3xl transition-all hover:scale-105 cursor-pointer relative overflow-hidden"
            onMouseEnter={() => setHoveredCard(1)}
            onMouseLeave={() => setHoveredCard(null)}
            data-testid="zone-1-card"
          >
            <Badge className="absolute top-4 right-4 bg-emerald-500 text-white border-0 font-bold">
              POPULÆR
            </Badge>
            <div className="text-emerald-400 mb-4">
              <MapPin className="w-8 h-8" />
            </div>
            <h3 className="font-display font-bold text-2xl mb-2 text-white">Zone 1 — KBH</h3>
            <p className="text-sm text-white/60 mb-6">Postnumre 1000-2999</p>
            <div className="space-y-3 mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-display font-black text-emerald-400">99</span>
                <span className="text-xl text-white/60">kr</span>
              </div>
              <p className="text-sm text-white/70">Same-day · Fast pris uanset distance</p>
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> 18:00 - 22:30</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Direkte A → B levering</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Express: 179 kr</li>
            </ul>
          </Card>

          {/* Zone 2 */}
          <Card
            className="bg-white/5 backdrop-blur-sm border-white/20 p-8 rounded-3xl transition-all hover:scale-105 cursor-pointer"
            onMouseEnter={() => setHoveredCard(2)}
            onMouseLeave={() => setHoveredCard(null)}
            data-testid="zone-2-card"
          >
            <div className="text-amber-400 mb-4">
              <MapPin className="w-8 h-8" />
            </div>
            <h3 className="font-display font-bold text-2xl mb-2 text-white">Zone 2 — Stor-KBH</h3>
            <p className="text-sm text-white/60 mb-6">Nordsjælland · Roskilde · Køge</p>
            <div className="space-y-3 mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-display font-black text-amber-400">199</span>
                <span className="text-xl text-white/60">kr</span>
              </div>
              <p className="text-sm text-white/70">Same-day · Op til 25 km inkluderet</p>
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400" /> +8 kr/km efter 25 km</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400" /> Hillerød, Helsingør, Borup</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400" /> Express: 289 kr</li>
            </ul>
          </Card>

          {/* Zone 3 */}
          <Card
            className="bg-white/5 backdrop-blur-sm border-white/20 p-8 rounded-3xl transition-all hover:scale-105 cursor-pointer"
            onMouseEnter={() => setHoveredCard(3)}
            onMouseLeave={() => setHoveredCard(null)}
            data-testid="zone-3-card"
          >
            <div className="text-red-400 mb-4">
              <MapPin className="w-8 h-8" />
            </div>
            <h3 className="font-display font-bold text-2xl mb-2 text-white">Zone 3 — Sjælland</h3>
            <p className="text-sm text-white/60 mb-6">Slagelse · Holbæk · Næstved · Lolland</p>
            <div className="space-y-3 mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-display font-black text-red-400">399</span>
                <span className="text-xl text-white/60">kr</span>
              </div>
              <p className="text-sm text-white/70">Same-day · Op til 50 km inkluderet</p>
            </div>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-red-400" /> +15 kr/km efter 50 km</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-red-400" /> Hele Sjælland</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-red-400" /> Express: 549 kr</li>
            </ul>
          </Card>
        </div>
      </section>

      {/* Why us */}
      <section className="container mx-auto px-4 py-16 max-w-5xl">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center" data-testid="feature-eco">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="font-display font-bold text-xl mb-2">Direkte levering</h3>
            <p className="text-white/60 text-sm">Vi henter og kører din pakke direkte til modtageren — ingen lager eller omlastning.</p>
          </div>
          <div className="text-center" data-testid="feature-speed">
            <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="font-display font-bold text-xl mb-2">Samme dag</h3>
            <p className="text-white/60 text-sm">Book inden 17:00 — vi leverer mellem 18:00 og 22:30.</p>
          </div>
          <div className="text-center" data-testid="feature-tracking">
            <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Truck className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="font-display font-bold text-xl mb-2">Live tracking</h3>
            <p className="text-white/60 text-sm">Følg din pakke i realtid på kortet hele vejen.</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 py-20 max-w-3xl text-center">
        <h2 className="font-display font-bold text-4xl mb-6">Klar til at sende?</h2>
        <p className="text-xl text-white/60 mb-8">
          Som Genfind-kunde får du 5% rabat på din første booking. Tag det med.
        </p>
        <Button
          onClick={goToBooking}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-7 rounded-2xl text-lg font-bold shadow-xl shadow-emerald-500/30 transition-all hover:scale-105"
          data-testid="genfind-final-cta"
        >
          Start din booking
          <ArrowRight className="ml-3 w-5 h-5" />
        </Button>
        <div className="mt-8 text-sm text-white/40">
          Rabat anvendes automatisk · Gælder 1 booking pr. kunde · Permanent samarbejde
        </div>
      </section>

      <div className="text-center pb-12">
        <Link to="/" className="text-white/40 hover:text-white text-sm" data-testid="genfind-back-link">
          ← Tilbage til emakurer.dk
        </Link>
      </div>
    </div>
  );
};

export default GenfindLandingPage;
